import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X, FileText } from "lucide-react";
import api, { downloadBlob } from "@/lib/api";

export default function DocPreview({ doc, onClose }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (doc.blobUrl) { setUrl(doc.blobUrl); return; }
    let obj;
    api.get(`/documents/${doc.id}/download`, { responseType: "blob" })
      .then((r) => {
        obj = URL.createObjectURL(new Blob([r.data], { type: doc.content_type || "application/octet-stream" }));
        setUrl(obj);
      })
      .catch(() => setErr("Unable to load document from cloud storage."));
    return () => obj && URL.revokeObjectURL(obj);
  }, [doc]);

  const saveFile = () => {
    if (doc.blobUrl) {
      const a = document.createElement("a");
      a.href = doc.blobUrl;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      downloadBlob(`/documents/${doc.id}/download`, doc.filename);
    }
  };

  const isPdf = (doc.content_type || "").includes("pdf") || doc.filename?.toLowerCase().endsWith(".pdf");
  const isImage = (doc.content_type || "").startsWith("image/");

  return createPortal(
    <div data-testid="doc-preview-backdrop" className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 sm:p-6" onClick={onClose}>
      <div data-testid="doc-preview" className="flex h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-[0_20px_50px_rgba(0,0,0,0.22)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-[#faf9f8] px-4 py-3 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="bdx-icon-tile"><FileText size={16} strokeWidth={1.8} /></span>
            <div className="min-w-0">
              <div className="bdx-label">Document Viewer</div>
              <div data-testid="doc-preview-title" className="truncate font-heading text-sm font-bold text-gray-950">{doc.filename}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button data-testid="doc-preview-download" className="bdx-icon-btn border-gray-200 bg-white" title="Download / Save" aria-label="Download document" onClick={saveFile}>
              <Download size={15} strokeWidth={1.7} />
            </button>
            <button data-testid="doc-preview-close" className="bdx-icon-btn border-gray-200 bg-white" onClick={onClose} aria-label="Close document viewer">
              <X size={16} strokeWidth={1.7} />
            </button>
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center overflow-auto bg-[#e9e9e7] p-2 sm:p-4">
          {err && <div data-testid="doc-preview-error" className="rounded-md border border-red-200 bg-red-50 p-5 text-sm text-red-700">{err}</div>}
          {!err && !url && <div data-testid="doc-preview-loading" className="text-sm text-gray-500">Loading document…</div>}
          {url && isPdf && <iframe data-testid="doc-preview-pdf" title={doc.filename} src={url} className="h-full w-full border-0 bg-white shadow-sm" />}
          {url && isImage && <img data-testid="doc-preview-image" src={url} alt={doc.filename} className="max-h-full max-w-full object-contain shadow-sm" />}
          {url && !isPdf && !isImage && (
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
              <p className="mb-4 text-sm text-gray-600">Preview is not available for this file type.</p>
              <button data-testid="doc-preview-fallback-download" className="bdx-btn-primary" onClick={saveFile}>
                <Download size={15} strokeWidth={1.7} /> Download {doc.filename}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}